import React, { useState, useEffect } from 'react';
import _ from 'lodash';
import './PrevetDecision.scss';
import MerchantDetailsCard from '../MerchantDetailsCard';
import { Tooltip as ReactTooltip } from 'react-tooltip';
import ToolTipIcon from '../../../images/toolTipIcon';
import { EmvioLoadingSpinner as LoadingSpinner } from '@nexio/emvio-util-ui';

const DECISION_DETAIL_MAPPER = {
    0: 'Approval Likely',
    1: 'Approval Conditional',
    2: 'Approval Unlikely'
};

const toolTipContentInfo = <div>Click on any item to see more info about the request.  For each reason, if any of the conditions apply we need the info to be provided.</div>;
const toolTipContentDocs = <div>Click on any item to see more info about the request. For each reason, if any of the conditions apply we need the document requested.</div>;

const PrevetDisclaimer = () => (
    <p className="prevet-disclaimer">
        {/* eslint-disable-next-line @stylistic/max-len */}
        This pre-vet result is preliminary only and assumes all information provided is true and complete. Final underwriting decisions depend on the signer’s credit history, MATCH and OFAC screening, business history and legitimacy, accuracy of submitted information, and the absence of fraud, regulatory issues, or serious derogatory findings. E-commerce merchants are subject to additional requirements.
        {/* {' '} */}
        {/* <a */}
        {/* // TODO: Add the link to the complete website review guidelines */}
        {/*     href="/home" */}
        {/*     target="_blank" */}
        {/*     rel="noreferrer" */}
        {/*     className="prevet-guidelines-link" */}
        {/*     aria-label="View the complete website review guidelines" */}
        {/* > */}
        {/*     Click here */}
        {/* </a> */}
        {/* {' '} */}
        {/* to view the complete website review guidelines. */}
    </p>
);

export const PrevetDecision = ({ merchantDetails, loading }) => {
    const [status, setStatus] = useState(merchantDetails.detail.status || '');
    const [decision, setDecision] = useState(_.get(merchantDetails, 'prevetData.decisionOutcome', ''));

    useEffect(() => {
        setStatus(merchantDetails.detail.status);
    }, [merchantDetails.detail.status]);

    useEffect(() => {
        setDecision(_.get(merchantDetails, 'prevetData.decisionOutcome', ''));
    }, [merchantDetails]);

    const renderDecisionGraphic = (decision) => {
        if (_.isNull(decision) || _.isUndefined(decision)) {
            return null;
        }
        const decisionText = DECISION_DETAIL_MAPPER[decision] || decision;
        const decisionClass = decisionText.toLowerCase().replace(/\s+/g, '-');

        return (
            <div className={`prevet-decision-badge prevet-decision-${decisionClass}`}>
                {decisionText}
            </div>
        );
    };

    const renderDocsRequestedList = (docsRequested) => {
        if (!docsRequested || !Array.isArray(docsRequested)) {
            return null;
        }

        return (
            <ul className="prevet-list">
                {docsRequested.map((doc, index) => (
                    <li key={doc.title}>
                        <a
                            className="doc-title"
                            tabIndex="0"
                            role="button"
                            key={doc.title}
                            data-tooltip-id={`${index}-doc-tooltip`}
                        >
                            {doc.title}
                        </a>
                        <ReactTooltip
                            className="explanation-tool-tip-content"
                            id={`${index}-doc-tooltip`}
                            openOnClick={true}
                            place="right"
                            variant="light"
                            effect="solid"
                            content={
                                <div className="explanation-tool-tip-content-container">
                                    {doc.description.map((item, index) => (
                                        <div key={`${item}-info-tooltip-`} >
                                            <p className="reason-header">Reason {`${doc.description.length > 1 ? index + 1 : ''}`}</p>
                                            <ul>
                                                <li className="reason-description-items">{_.upperFirst(item)}</li>
                                            </ul>
                                        </div>
                                    ))}
                                </div>
                            }
                        />
                    </li>
                ))}
            </ul>
        );
    };

    const renderInfoRequestedList = (infoRequested) => {
        if (!infoRequested || !Array.isArray(infoRequested)) {
            return null;
        }

        return (
            <ul className="prevet-list">
                {infoRequested.map((info, index) => (
                    <li key={info.title}>
                        <a
                            className="doc-title"
                            role="button"
                            tabIndex="0"
                            key={info.title}
                            data-tooltip-id={`${index}-info-tooltip`}
                        >
                            {info.title}
                        </a>
                        <ReactTooltip
                            className="explanation-tool-tip-content"
                            id={`${index}-info-tooltip`}
                            openOnClick={true}
                            place="right"
                            variant="light"
                            effect="solid"
                            content={
                                <div className="explanation-tool-tip-content-container">
                                    {info.description.map((item, index) => (
                                        <div key={`${item}-info-tooltip`} >
                                            <p className="reason-header">Reason {`${info.description.length > 1 ? index + 1 : ''}`}</p>
                                            <ul>
                                                <li className="reason-description-items">{_.upperFirst(item)}</li>
                                            </ul>
                                        </div>
                                    ))}
                                </div>
                            }
                        />
                    </li>
                ))}
            </ul>
        );
    };

    const renderRejectConditions = (rejectConditions) => {
        if (!rejectConditions || !Array.isArray(rejectConditions) || rejectConditions.length === 0) {
            return null;
        }

        return (
            <>
                {rejectConditions.map((reasonGroup, index) => {
                    // Skip if reasonGroup is not an array
                    if (!Array.isArray(reasonGroup) || reasonGroup.length === 0) {
                        return null;
                    }

                    return (
                        // eslint-disable-next-line @eslint-react/no-array-index-key
                        <div key={`reason-${index}`} className="prevet-field">
                            <label className="prevet-field-label">
                                Reason {index + 1}
                                <a
                                    className="prevet-info-icon"
                                    data-tooltip-id={`reason-${index}-tooltip`}
                                    data-tip
                                    data-tooltip-variant="light"
                                    aria-label={`Reason ${index + 1}`}
                                    role="button"
                                    tabIndex="0"
                                >
                                    <ToolTipIcon fill="#595D63" />
                                </a>
                                <ReactTooltip
                                    className="explanation-tool-tip-content"
                                    id={`reason-${index}-tooltip`}
                                    place="right"
                                    variant="light"
                                    effect="solid"
                                    content="Set of conditions met that leads to a declined application."
                                />
                            </label>
                            <ul className="prevet-list">
                                {reasonGroup.map((bullet, bulletIndex) => (
                                    // eslint-disable-next-line @eslint-react/no-array-index-key
                                    <li key={`bullet-${index}-${bulletIndex}`}>
                                        {typeof bullet === 'string' ? bullet : String(bullet)}
                                    </li>
                                ))}
                            </ul>
                        </div>
                    );
                })}
            </>
        );
    };

    const infoRequested = _.get(merchantDetails, 'prevetData.explanationMap["1"]', []);
    const docsRequested = _.get(merchantDetails, 'prevetData.explanationMap["0"]', []);
    const rejectConditions = _.get(merchantDetails, 'prevetData.explanationMap.rejectConditions', []);
    const isUnlikely = decision === 2 || decision === 'Approval Unlikely';

    return (
        loading ? (
            <div className="prevet-decision-loading">
                <LoadingSpinner />
            </div>
        ) : (
            <MerchantDetailsCard title="Pre-Vet Decision" startOpen={status === 'prevet' ? true : false}>
                <div className="prevet-decision-content">
                    <div className="prevet-list">
                        <div className="prevet-field">
                            <label className="prevet-field-label">Decision</label>
                            {renderDecisionGraphic(decision)}
                        </div>

                        {infoRequested.length > 0 && (
                            <div className="prevet-field">
                                <label className="prevet-field-label">
                                    Info Requested
                                    <a
                                        className="prevet-info-icon"
                                        data-tooltip-id="more-info-tooltip"
                                        data-tip data-tooltip-variant="light"
                                        data-for="toolTip"
                                        aria-label="Info Requested"
                                        role="button"
                                        tabIndex="0"
                                    >
                                        <ToolTipIcon fill="#595D63" />
                                    </a>
                                    <ReactTooltip
                                        className="explanation-tool-tip-content"
                                        id="more-info-tooltip"
                                        place="right"
                                        variant="light"
                                        effect="solid"
                                        content={toolTipContentInfo}
                                    />
                                </label>
                                {renderInfoRequestedList(merchantDetails.prevetData.explanationMap['1'])}
                            </div>
                        )}
                        {docsRequested.length > 0 && (
                            <div className="prevet-field">
                                <label className="prevet-field-label">
                                    Docs Requested
                                    <a
                                        className="prevet-info-icon"
                                        data-tooltip-id="docs-tooltip"
                                        data-tip data-tooltip-variant="light"
                                        data-for="toolTip"
                                        aria-label="Docs Requested"
                                        role="button"
                                        tabIndex="0"
                                    >
                                        <ToolTipIcon fill="#595D63" />
                                    </a>
                                    <ReactTooltip
                                        className="explanation-tool-tip-content"
                                        id="docs-tooltip"
                                        place="right"
                                        variant="light"
                                        effect="solid"
                                        content={toolTipContentDocs}
                                    />
                                </label>
                                {renderDocsRequestedList(merchantDetails.prevetData.explanationMap['0'])}
                            </div>
                        )}
                        {isUnlikely && rejectConditions.length > 0 && renderRejectConditions(rejectConditions)}
                        <div className="prevet-field">
                            <label className="prevet-field-label">Disclaimer</label>
                            {/* TODO: Add a link to the complete website review guidelines */}
                            <PrevetDisclaimer />
                        </div>
                    </div>
                </div>
            </MerchantDetailsCard>
        )
    );
};

export default PrevetDecision;
